import { Check, Circle, MapPin, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ReservationCardConfig } from 'app/pages/Restaurants/config';
import { DEFAULT_RESTAURANT_IMAGE } from 'app/pages/Restaurants/constants';
import StyledButton from '@/app/components/StyledButton';
import {
  loadReservationProductFromStoreApis,
  buildRestaurantDetailUrl,
  useReservationStore,
} from 'app/pages/Restaurants/store';

type Props = {
  restaurants: ReservationCardConfig[];
  t: (key: string, fallback: string) => string;
};
const capitalize = (str) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

/**
 * Tách riêng picker để flow đặt bàn sau thanh toán có thể tự quản lý modal,
 * selection và điều hướng mà không làm PaymentSuccess phụ thuộc vào UI chi tiết.
 */
export const RestaurantBookingPicker = ({ restaurants, t }: Props) => {
  const history = useHistory();
  const searchCondition = useReservationStore(state => state.searchCondition);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>();
  const [reservationProductsByStoreId, setReservationProductsByStoreId] =
    useState<Record<string, any>>({});
  const availableRestaurants = useMemo(
    () =>
      restaurants
        .filter(item => !item.isFullyBooked)
        .map(restaurant => {
          const product = reservationProductsByStoreId[String(restaurant.id)];
          const variant = product?.variants?.find(
            (candidate: any) =>
              String(candidate?.storeId ?? '') === String(restaurant.id),
          );

          return variant
            ? { ...restaurant, ...variant, id: restaurant.id }
            : restaurant;
        }),
    [restaurants, reservationProductsByStoreId],
  );

  useEffect(() => {
    if (!availableRestaurants.length) return;

    let isCancelled = false;
    const loadRestaurants = async () => {
      await Promise.all(
        availableRestaurants.map(async restaurant => {
          try {
            const reservationProduct = await loadReservationProductFromStoreApis(
              searchCondition,
              restaurant.id,
            );
            if (!isCancelled) {
              setReservationProductsByStoreId(current => ({
                ...current,
                [String(restaurant.id)]: reservationProduct,
              }));
            }
          } catch (error) {
            if (!isCancelled) {
              console.error(
                `Failed to load restaurant reservation product (${restaurant.id}):`,
                error,
              );
            }
          }
        }),
      );
    };

    loadRestaurants();
    return () => {
      isCancelled = true;
    };
  }, [restaurants, searchCondition]);

  useEffect(() => {
    if (selectedId && !availableRestaurants.some(item => item.id === selectedId)) {
      setSelectedId(undefined);
    }
  }, [availableRestaurants, selectedId]);

  const handleBooking = () => {
    if (!selectedId) return;
    setOpen(false);
    const selectedRestaurant = availableRestaurants.find(
      item => String(item.id) === String(selectedId),
    );
    history.push(
      buildRestaurantDetailUrl(
        selectedId,
        selectedRestaurant?.bookingRules ?? selectedRestaurant?.booking_rules,
      ),
    );
  };

  if (!availableRestaurants.length) return null;

  return (
    <>
      <StyledButton
        textColor="white"
        bgColor="linear-gradient(79.97deg, #f06f24 -7.79%, #f8b117 107.61%)"
        style={{
          width: 'auto',
          fontSize: 16,
          fontWeight: 600,
          borderRadius: 5,
          minHeight: 40,
        }}
        border="none"
        onClick={() => setOpen(true)}
      >
        <span className="--textBtn" style={{ marginRight: 8 }}>
          {capitalize(t('b2b.restaurant.booking.reservationTitle', 'Đặt bàn nhà hàng'))}
          {' '}
        </span>
        <i className="fa-solid fa-arrow-right"></i>
      </StyledButton>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent hideCloseButton className="max-w-[450px] overflow-hidden rounded-xl border-0 bg-white p-0 shadow-[0_18px_50px_rgba(0,0,0,0.18)]">
          <DialogHeader className="px-6 pb-2 pt-5">
            <DialogTitle className="text-[16px] font-bold leading-6 text-[#111111]">
              {t('b2b.restaurant.booking.availableRestaurants', 'Danh sách nhà hàng')}
            </DialogTitle>
          </DialogHeader>
          <DialogClose aria-label={t('b2b.close', 'Đóng')} className="absolute right-5 top-5 border-0 bg-transparent p-0 text-[#222222] shadow-none outline-none transition-opacity hover:bg-transparent hover:opacity-60 focus:bg-transparent focus:outline-none focus:ring-0 focus:ring-offset-0">
            <X className="h-5 w-5" strokeWidth={1.8} />
          </DialogClose>
          <div className="max-h-[52vh] overflow-y-auto px-6 py-1">
            {availableRestaurants.map(item => {
              const isSelected = item.id === selectedId;
              return (
                <button
                  key={item.id}
                  type="button"
                  className="flex min-h-[68px] w-full items-center gap-3 border-0 border-b border-dashed border-[#e5e5e5] bg-white py-2 text-left last:border-b-0 hover:bg-white"
                  onClick={() => setSelectedId(item.id)}
                >
                  <img
                    src={item.image || item.imageUrl || DEFAULT_RESTAURANT_IMAGE}
                    alt={item.titleKey}
                    onError={event => {
                      event.currentTarget.src = DEFAULT_RESTAURANT_IMAGE;
                    }}
                    className="h-[50px] w-[68px] shrink-0 rounded-md bg-white object-contain"
                  />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span
                      className="truncate text-[13px] font-medium leading-5 text-[#111111]"
                      title={item.storeName}
                    >
                      {item.storeName || item.titleKey}
                    </span>
                    {item.siteName && (
                      <span className="flex min-w-0 items-center gap-1 text-[12px] font-normal leading-5 text-[#8c8c8c]">
                        <MapPin
                          className="!h-[14px] !w-[14px] shrink-0 !text-[#8c8c8c]"
                          strokeWidth={1.8}
                          style={{ width: 14, height: 14, color: '#8c8c8c' }}
                        />
                        <span className="truncate !text-[#8c8c8c]" title={item.siteName}>
                          {item.siteName}
                        </span>
                      </span>
                    )}
                  </span>
                  {isSelected ? (
                    <span className="flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-[#55a56b] text-white">
                      <Check className="h-4 w-4" strokeWidth={3} />
                    </span>
                  ) : (
                    <Circle className="h-4 w-4 shrink-0 text-[#e6e6e6]" />
                  )}
                </button>
              );
            })}
          </div>
          <div className="flex justify-center px-6 pb-5 pt-5">
            <Button
              type="button"
              variant={selectedId ? 'default' : 'fullyBooked'}
              className="!h-[34px] min-w-[132px] rounded-lg px-6 text-xs font-semibold"
              onClick={handleBooking}
              disabled={!selectedId}
            >
              {t('b2b.restaurant.booking.book', 'Đặt bàn')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
